// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// The app-side Merlin client (WS4 Phase 1c-ii). Implements the diagram's
// backend-agnostic ProposeFn by POSTing to the server's /api/merlin/propose and
// mapping the MerlinOutcome JSON onto a client ProposeResult (the conversation
// result + the model to apply on accept). Kept in the app, not the diagram
// package, so @simlin/diagram carries no server-API dependency.
//
// Never throws: a network error, a non-2xx status, or a malformed body all map
// to a graceful `unavailable` result the Agents panel renders as an error turn
// (E2 / H6) -- the thread stays intact. Copy here is a placeholder pending the
// Coherence ST4AI-voice set.

import type { JsonModel } from '@simlin/engine';
import type { ProposalSummary, ProposeFn, ProposeResult } from '@simlin/diagram/agent-conversation';

const DEFAULT_UNAVAIL = 'The build assistant is unavailable right now.';
const MERLIN_PROPOSE_PATH = '/api/merlin/propose';

function str(x: unknown, fallback: string): string {
  return typeof x === 'string' && x.trim().length > 0 ? x : fallback;
}

function strArray(x: unknown): string[] {
  return Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : [];
}

function isJsonModel(x: unknown): x is JsonModel {
  const m = x as JsonModel | null;
  return (
    !!m &&
    typeof m === 'object' &&
    Array.isArray(m.stocks) &&
    Array.isArray(m.flows) &&
    Array.isArray(m.auxiliaries)
  );
}

function message(text: string): ProposeResult {
  return { result: { kind: 'message', text } };
}
function unavailable(reason: string): ProposeResult {
  return { result: { kind: 'unavailable', reason } };
}

/**
 * Map an HTTP status + parsed body from /api/merlin/propose onto a ProposeResult
 * (pure). Only a 200 `proposal` carrying a usable model becomes a proposal the
 * user can apply; clarify/decline become messages; everything else (tier block,
 * unconfigured service, bad status, junk body) is a graceful unavailable.
 */
export function mapOutcome(status: number, data: unknown, genId: () => string): ProposeResult {
  const o: Record<string, unknown> = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};

  if (status === 200) {
    if (o.kind === 'proposal') {
      const rawDiff = (o.diff && typeof o.diff === 'object' ? o.diff : {}) as Record<string, unknown>;
      const proposal: ProposalSummary = {
        proposalId: genId(),
        summary: str(o.summary, 'Proposed a change.'),
        added: strArray(rawDiff.added),
        changed: strArray(rawDiff.changed),
        removed: strArray(rawDiff.removed),
      };
      // A proposal with no usable model can't be applied -- degrade to a message
      // rather than offer an accept that would do nothing.
      if (!isJsonModel(o.model)) {
        return message(proposal.summary);
      }
      return { result: { kind: 'proposal', proposal }, proposedModel: o.model };
    }
    if (o.kind === 'clarify') {
      return message(str(o.question, 'Could you say a bit more about what you want to change?'));
    }
    if (o.kind === 'decline') {
      return message(str(o.reason, 'Merlin could not act on that request.'));
    }
    if (o.kind === 'unavailable') {
      return unavailable(str(o.reason, DEFAULT_UNAVAIL));
    }
  }

  if (status === 403) {
    // H7: gated below Merlin's tier -- surface the upgrade path.
    return unavailable('Merlin needs the Student tier or higher.');
  }
  if (status === 503) {
    return unavailable(str(o.reason, DEFAULT_UNAVAIL));
  }
  return unavailable(DEFAULT_UNAVAIL);
}

let idCounter = 0;
function defaultGenId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  idCounter += 1;
  return `proposal-${idCounter}`;
}

export interface MerlinProposerDeps {
  /** Injectable fetch (defaults to global fetch) -- the seam for tests. */
  fetchFn?: typeof fetch;
  /** Proposal-id generator (defaults to crypto.randomUUID with a counter fallback). */
  genId?: () => string;
  /** Endpoint path (defaults to /api/merlin/propose). */
  path?: string;
}

/**
 * Build the app's ProposeFn -- one POST to the server, mapped to a ProposeResult.
 * Injected into the Editor as a prop (like onSave), keeping the diagram package
 * free of any server-API knowledge.
 */
export function createMerlinProposer(deps: MerlinProposerDeps = {}): ProposeFn {
  const doFetch = deps.fetchFn ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const genId = deps.genId ?? defaultGenId;
  const path = deps.path ?? MERLIN_PROPOSE_PATH;

  return async ({ prompt, model, modelName, history }) => {
    let res: Response;
    try {
      res = await doFetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prompt, model, modelName, history }),
      });
    } catch {
      // Network failure: graceful, never a thrown rejection into the UI.
      return unavailable(DEFAULT_UNAVAIL);
    }

    let data: unknown;
    try {
      data = await res.json();
    } catch {
      data = undefined;
    }
    return mapOutcome(res.status, data, genId);
  };
}
